import React, { useContext } from 'react';
import { AppShell, Container, createStyles, Flex, Skeleton } from '@mantine/core';
import { isMobile } from 'react-device-detect';

import UnknownErrorScreen from '~/domains/common/components/UnknownErrorScreen/UnknownErrorScreen';
import { UserContext } from '~/components/providers/UserProvider';
import type { BRANDS } from '~/domains/auth/consts';

import { ContestManageContextProvider } from '../../context/ContestManageContext';
import ContestManageModalsContextProvider from '../Modals/ContestManageModalsProvider';
import { Sections } from '../../sections/Sections';
import { SectionHeader } from '../Header/Header';
import { SectionSkeleton } from '../../sections/Sections/SectionSkeleton';

const MIN_HEIGHT = 150;

const useSectionStyles = createStyles((theme) => ({
  pageWrapper: {
    [theme.fn.largerThan('md')]: {
      paddingTop: theme.spacing.md,
      paddingBottom: theme.spacing.md,
    },
  },
  container: {
    position: 'relative',
  },
}));

export type ContestManagePageProps = {
  data?: {
    isUnifiedFlow: boolean;
    allowedTagNames?: string[];
    sectionsData?: unknown[];
    titleConfig?: {
      headerTitle: string;
      showSubtitle: boolean;
      reviewTitle: string;
    };
    submitConfig?: {
      submitButtonText: string;
    };
    contestId?: string;
    data?: {
      leagueId?: string;
      contestTemplateId?: string;
    };
    restartData?: {
      brand?: BRANDS;
      leagueId?: string;
      contestTemplateId?: string;
      needsEORestart?: boolean;
    };
    commissioner?: {
      id: string;
      username: string;
      handle: string;
      avatarUrl?: string;
    };
  };
  isLoading: boolean;
  error: unknown;
  operation: 'create' | 'edit' | 'restart';
  legacyComponent: React.ComponentType;
  loadingTitle: string;
};

export function ContestManagePage({
  data,
  isLoading,
  error,
  operation,
  legacyComponent: LegacyComponent,
  loadingTitle,
}: ContestManagePageProps) {
  const { classes } = useSectionStyles();
  const { user } = useContext(UserContext);

  if (operation === 'edit' && data && !isLoading && data.commissioner && user) {
    if (data.commissioner.id !== user.id) {
      return (
        <AppShell header={<SectionHeader title={loadingTitle} />} styles={{ main: { padding: 0 } }}>
          <UnknownErrorScreen statusCode={403} isShowHomeButton />
        </AppShell>
      );
    }
  }

  if (error) {
    return (
      <AppShell header={<SectionHeader title={loadingTitle} />} styles={{ main: { padding: 0 } }}>
        <UnknownErrorScreen statusCode={404} isShowHomeButton />
      </AppShell>
    );
  }

  if (isLoading) {
    return (
      <AppShell header={<SectionHeader title={loadingTitle} />} styles={{ main: { padding: 0 } }}>
        <div className={classes.pageWrapper}>
          <Flex justify="center" pt="md" w="100%">
            <Skeleton h={48} w={250} />
          </Flex>
          <Container size="md" /* eslint-disable-line local-rules/use-theme-tokens */ pt={40} className={classes.container}>
            <Flex
              justify="space-between"
              /* eslint-disable-line local-rules/use-theme-tokens */ gap={20}
              direction="column"
              mih={`calc(100vh - ${isMobile ? 16 : MIN_HEIGHT}px)`}
              pt="md"
            >
              <SectionSkeleton />
            </Flex>
          </Container>
        </div>
      </AppShell>
    );
  }

  if (!data?.isUnifiedFlow) {
    return <LegacyComponent />;
  }

  const getSectionsProps = () => {
    const baseProps = {
      operation,
      titleConfig: data.titleConfig,
      submitConfig: data.submitConfig,
    };

    switch (operation) {
      case 'create':
        return {
          ...baseProps,
          leagueId: data.data?.leagueId,
          contestTemplateId: data.data?.contestTemplateId,
        };
      case 'edit':
        return {
          ...baseProps,
          contestId: data.contestId,
        };
      case 'restart':
        return {
          ...baseProps,
          contestId: data.contestId,
          brand: data.restartData?.brand,
          leagueId: data.restartData?.leagueId,
          contestTemplateId: data.restartData?.contestTemplateId,
          eoNeedsRestart: data.restartData?.needsEORestart,
        };
      default:
        return baseProps;
    }
  };

  return (
    <ContestManageContextProvider
      allowedTagNames={data.allowedTagNames}
      sectionsData={data.sectionsData}
    >
      <ContestManageModalsContextProvider>
        <Sections {...getSectionsProps()} />
      </ContestManageModalsContextProvider>
    </ContestManageContextProvider>
  );
}
